import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setAuthToken, clearAuthToken } from '../config/axios';
import configService from '../services/configService';

const Auth0Context = createContext();

export const useAuth = () => {
  const context = useContext(Auth0Context);
  if (!context) {
    throw new Error('useAuth must be used within an Auth0Provider');
  }
  return context;
};

export const Auth0ContextProvider = ({ children }) => {
  const {
    user: auth0User,
    isAuthenticated: auth0IsAuthenticated,
    isLoading,
    error,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently
  } = useAuth0();

  const [config, setConfig] = useState(null);
  const [tokenReady, setTokenReady] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [token, setToken] = useState(null);

  // Load configuration
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const appConfig = await configService.getConfig();
        setConfig(appConfig);
      } catch (error) {
        console.error('Error loading config:', error);
      }
    };
    loadConfig();
  }, []);

  // Monitor for Auth0 errors and clean URL without logout
  useEffect(() => {
    // Check if we have an Auth0 error in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const urlError = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (urlError) {
      console.error('🚨 Auth0 error detected in URL:', { error: urlError, description: errorDescription });

      // Clear Auth0 cache
      try {
        Object.keys(localStorage).forEach(key => {
          if (key.includes('@@auth0spajs@@')) {
            localStorage.removeItem(key);
          }
        });

        // Also clear session storage
        Object.keys(sessionStorage).forEach(key => {
          if (key.includes('@@auth0spajs@@')) {
            sessionStorage.removeItem(key);
          }
        });
      } catch (e) {
        console.warn('Could not clear Auth0 cache:', e);
      }

      // Clean URL without redirecting - let the login component show
      console.log('🧹 Cleaning error from URL, showing login page');
      window.history.replaceState({}, document.title, '/login');
    }

    // For Auth0 SDK errors, just log them - don't auto-redirect
    // The App component will handle showing appropriate UI
    if (error) {
      console.error('🚨 Auth0 SDK error detected:', error);
      console.log('ℹ️ Error will be handled by the UI components');
    }
  }, [error]);

  // Set up token management - LIMITED OFFLINE ACCESS (1 second refresh token expiry)
  useEffect(() => {
    const setupToken = async () => {
      console.log('🔄 Token setup effect triggered:', {
        isAuthenticated: auth0IsAuthenticated,
        isLoading,
        config: !!config,
        hasAuth0User: !!auth0User
      });

      if (auth0IsAuthenticated && !isLoading && config) {
        try {
          setTokenReady(false);
          setTokenError(null);
          console.log('🔄 Getting access token with config:', {
            audience: config.auth0.audience,
            domain: config.auth0.domain
          });

          // Add timeout to prevent hanging - increased to 30 seconds
          const tokenPromise = getAccessTokenSilently({
            authorizationParams: {
              audience: config.auth0.audience,
            },
            // Use cache for better performance
            cacheMode: 'on',
          });

          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Token acquisition timeout')), 30000)
          );

          const token = await Promise.race([tokenPromise, timeoutPromise]);

          console.log('✅ Access token obtained:', token.substring(0, 20) + '...');
          setAuthToken(token);
          setTokenReady(true);
          setToken(token);
          console.log('✅ Token ready and axios configured');

        } catch (error) {
          console.error('❌ Failed to get access token:', error);
          setTokenError(error.message);
          setTokenReady(false);

          // Clear any existing token
          clearAuthToken();

          // If token acquisition fails due to login_required or refresh token issues
          // clear cache but DON'T call logout - let the user navigate naturally
          if (error.message.includes('login_required') ||
              error.message.includes('Missing Refresh Token') ||
              error.message.includes('refresh_token') ||
              error.error === 'login_required' ||
              error.error === 'invalid_grant') {
            console.log('🔄 Token failed, clearing cache. User will need to login again.');

            // Clear Auth0 cache
            try {
              // Clear all Auth0 related localStorage items
              Object.keys(localStorage).forEach(key => {
                if (key.includes('@@auth0spajs@@')) {
                  localStorage.removeItem(key);
                }
              });

              // Clear session storage as well
              Object.keys(sessionStorage).forEach(key => {
                if (key.includes('@@auth0spajs@@')) {
                  sessionStorage.removeItem(key);
                }
              });

              console.log('✅ Auth0 cache cleared');

              // Redirect to login page directly without calling logout
              // This prevents the logout redirect loop
              window.location.href = '/login';
            } catch (cacheError) {
              console.warn('⚠️ Could not clear Auth0 cache:', cacheError);
            }
          }
        }
      } else {
        console.log('⏳ Token setup skipped:', {
          isAuthenticated: auth0IsAuthenticated,
          isLoading,
          hasConfig: !!config,
          reason: !auth0IsAuthenticated ? 'not authenticated' : isLoading ? 'still loading' : 'no config'
        });
        setTokenReady(false);
      }
    };

    setupToken();
  }, [auth0IsAuthenticated, isLoading, config, getAccessTokenSilently, auth0User]);

  // Transform Auth0 user to match existing interface
  const user = auth0User ? {
    id: auth0User.sub,
    email: auth0User.email,
    name: auth0User.name,
    role: auth0User['https://timesheet.app/role'] || 'user',
    permissions: auth0User['https://timesheet.app/permissions'] || 'user',
    department_id: auth0User['https://timesheet.app/department_id'],
    hourly_rate: auth0User['https://timesheet.app/hourly_rate'],
    employee_id: auth0User['https://timesheet.app/employee_id']
  } : null;

  const login = async (email, password) => {
    // Auth0 handles login via redirect, so we redirect to Auth0
    try {
      await loginWithRedirect({
        authorizationParams: {
          screen_hint: 'login'
        }
      });
      return { success: true };
    } catch (err) {
      console.error('Login error:', err);
      return { success: false, error: err.message };
    }
  };

  const logout = async () => {
    try {
      setTokenReady(false);
      clearAuthToken(); // Clear token before logout
      await auth0Logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      });
    } catch (err) {
      console.error('Logout error:', err);
      clearAuthToken(); // Ensure token is cleared even if logout fails
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    // Auth0 handles password changes through their interface
    // We can redirect to Auth0's password change page or use Management API
    try {
      if (!config) {
        throw new Error('Configuration not loaded');
      }

      // For now, we'll redirect to Auth0's password change page
      const domain = config.auth0.domain;
      const clientId = config.auth0.clientId;
      const returnTo = encodeURIComponent(window.location.origin);

      window.location.href = `https://${domain}/v2/logout?client_id=${clientId}&returnTo=${returnTo}`;

      return { success: true, message: 'Redirecting to password change page...' };
    } catch (err) {
      console.error('Password change error:', err);
      return { success: false, error: 'Password change failed' };
    }
  };

  // Enhanced authentication function - only true when token is ready
  const isAuthenticated = () => {
    console.log('🔍 Auth check:', {
      auth0Authenticated: auth0IsAuthenticated,
      tokenReady,
      hasToken: !!token,
      loading: isLoading,
      tokenError: !!tokenError
    });
    
    // Only consider authenticated if Auth0 says yes AND we have a token ready
    return auth0IsAuthenticated && tokenReady && !!token && !isLoading && !tokenError;
  };

  // Define permission levels that should have admin privileges
  const adminPermissions = ['admin', 'administrator', 'system_admin'];

  // Define roles that should have admin privileges
  const adminRoles = ['administrator', 'admin', 'Director', 'Manager', 'System Administrator'];

  const isAdmin = () => {
    if (!user) return false;

    // Check if user's permissions grant admin access
    const userPermissions = user.permissions;
    if (userPermissions && typeof userPermissions === 'string') {
      if (adminPermissions.includes(userPermissions.toLowerCase())) {
        return true;
      }
    }

    // Check if user's role grants admin access
    const userRole = user.role;
    if (userRole && typeof userRole === 'string') {
      if (adminRoles.includes(userRole)) {
        return true;
      }
    }

    return false;
  };

  const hasRole = (role) => {
    if (!user) return false;
    return user.role === role;
  };

  // Get access token for API calls - LIMITED OFFLINE ACCESS
  const getAccessToken = async () => {
    try {
      if (!config) {
        throw new Error('Configuration not loaded');
      }

      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: config.auth0.audience,
        },
      });
      return token;
    } catch (error) {
      console.error('Error getting access token:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading: isLoading || (auth0IsAuthenticated && !tokenReady && !tokenError),
    error: error || tokenError,
    login,
    logout,
    changePassword,
    isAuthenticated,
    isAdmin,
    hasRole,
    checkAuthStatus: isAuthenticated,
    getAccessToken,
    tokenReady,
    // Additional Auth0 specific methods
    loginWithRedirect,
    getAccessTokenSilently
  };

  return (
    <Auth0Context.Provider value={value}>
      {children}
    </Auth0Context.Provider>
  );
};
